import { multiStep } from '../MultiStepWizard/multiStep'
import { Formik } from 'formik'
import { ImagesField } from 'components/Wizards/SelectImages/ImagesField'
import { Form } from 'components/Wizards/Form/Form'
import { Images } from 'blaze-ui/icons'
import { ShowMoreImagesButton } from './ShowMoreImagesButton'
import Styles from './SelectImages.module.scss'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import cx from 'classnames'
import useEmblaCarousel from 'embla-carousel-react'
import { DEFAULT_OPTIONS } from 'components/Carousel/Carousel'
import { ResponsiveAddFromAnotherSourceButton } from 'components/Wizards/SelectImages/ResponsiveAddFromAnotherSourceButton'
import { StepProps } from 'components/Wizards/MultiStepWizard/Types'
import { useImages, useUnsplashImages } from 'components/Wizards/SelectImages/ImagesProvider'
import { useEffect } from 'react'
import { Text } from 'blaze-ui/Text/Text'
import { Checkbox } from 'blaze-ui/Checkbox/Checkbox'

const SelectImages = ({
  schema,
  onSubmit,
  initialValues,
  onBack,
  doNotIncludeImagesDefault,
}: StepProps & { doNotIncludeImagesDefault?: boolean }) => {
  const { setImages, abort, topic, generateImageKeywords } = useImages()
  const unsplash = useUnsplashImages()
  const noUnsplashResults = !unsplash.isLoading && !unsplash.isError && unsplash.photos.length === 0
  const { submitLabel, type, showIncludeImages } = schema.describe().meta
  const { isMobile } = useResponsiveDesign()
  const embla = useEmblaCarousel({ ...DEFAULT_OPTIONS })

  useEffect(() => {
    if (!topic && initialValues?.topic) {
      generateImageKeywords({ topic: initialValues.topic })
    }
  }, [topic, initialValues?.topic, generateImageKeywords])

  return (
    <Formik
      enableReinitialize
      validationSchema={schema}
      onSubmit={(values) => {
        abort()
        return onSubmit(values)
      }}
      initialValues={schema.cast({ ...schema.getDefault(), ...initialValues }, { assert: false })}
    >
      {({ values, setFieldValue }) => {
        const isDisabled = (values as any)?.selected_images?.length > 3
        const includeImages = (values as any)?.include_images

        // Default to the prop value, but override if the form value is explicitly set
        let doNotIncludeImages = doNotIncludeImagesDefault
        if (includeImages === true) {
          doNotIncludeImages = false
        } else if (includeImages === false) {
          doNotIncludeImages = true
        }

        return (
          <Form.Root>
            <Form.Section>
              <Form.SectionContent>
                <Form.Fields className={Styles.fields}>
                  <div className={Styles.imagesField}>
                    <ImagesField embla={embla} type={type} />
                    <div className={cx(Styles.buttons, Styles.buttonGroup)}>
                      <div className={Styles.buttons}>
                        {!noUnsplashResults && (
                          <ShowMoreImagesButton
                            onShowMore={() => {
                              const { scrollSnapList, scrollTo } = embla[1] || {}
                              scrollTo?.((scrollSnapList?.() || []).length - 3)
                            }}
                            fullWidth={isMobile}
                            variant={isMobile ? 'tertiary' : 'secondary'}
                            size={isMobile ? 'lg' : 'md'}
                            frontIcon={isMobile ? Images : undefined}
                          />
                        )}
                        <ResponsiveAddFromAnotherSourceButton
                          onAdd={(added) => {
                            const { scrollTo, scrollSnapList } = embla[1] || {}
                            scrollTo?.((scrollSnapList?.() || []).length - added)
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </Form.Fields>
              </Form.SectionContent>
            </Form.Section>
            <Form.Actions isSticky>
              {!isMobile && (
                <Form.PreviousButton
                  onPress={() => {
                    abort()
                    setImages([])
                    onBack?.()
                  }}
                  aria-label="Context"
                >
                  Context
                </Form.PreviousButton>
              )}
              <Form.PrimaryActions>
                {isDisabled && (
                  <Text variant="metadata" color="var(--red-50)">
                    Maximum 3 Images
                  </Text>
                )}
                {showIncludeImages && (
                  <div className={Styles.doNotIncludeContainer}>
                    <Checkbox
                      isSelected={doNotIncludeImages}
                      onChange={(checked) => {
                        setFieldValue('include_images', !checked)
                      }}
                      name="do_not_include_images"
                    >
                      <Text variant="smallList">Do not include any images</Text>
                    </Checkbox>
                  </div>
                )}
                <Form.SubmitButton endIcon={() => <></>} isDisabled={isDisabled}>
                  {submitLabel}
                </Form.SubmitButton>
              </Form.PrimaryActions>
            </Form.Actions>
          </Form.Root>
        )
      }}
    </Formik>
  )
}

const MultiStep = multiStep(SelectImages)

export { MultiStep as SelectImages }
